import { Link } from "react-router";

import { WorkThumb } from "../lib/WorkThumb.tsx";
import { DASHBOARD_COPY } from "./dashboard.constants.ts";
import type { ActivityRow, StripCard } from "./dashboard.model.ts";

/**
 * One work, photograph first.
 *
 * The whole card is the link: at this size the title is a small target, and
 * there is only one thing a card can do.
 */
export function WorkStrip({ cards }: { cards: StripCard[] }) {
  return (
    <ul className="dashboard__works">
      {cards.map((card) => (
        <li key={card.id} className="dashboard__work">
          <Link to={card.href} className="dashboard__work-link">
            <WorkThumb imageUrl={card.imageUrl} title={card.title} />

            <span className="dashboard__work-body">
              <span className="dashboard__work-title">{card.title}</span>
              <span className="dashboard__work-attribution">{card.attribution}</span>
              {card.medium && (
                <span className="dashboard__work-medium">{card.medium}</span>
              )}
            </span>
          </Link>
        </li>
      ))}
    </ul>
  );
}

export function ActivityList({ rows }: { rows: ActivityRow[] }) {
  return (
    <ul className="dashboard__activity">
      {rows.map((row) => (
        <li key={row.id} className="dashboard__activity-row">
          {/* A row about a work opens it; one about a collection has nowhere to
              go, and the model is what decided which. */}
          {row.href === null ? (
            <span className="dashboard__activity-link">
              <ActivityBody row={row} />
            </span>
          ) : (
            <Link to={row.href} className="dashboard__activity-link">
              <ActivityBody row={row} />
            </Link>
          )}
        </li>
      ))}
    </ul>
  );
}

function ActivityBody({ row }: { row: ActivityRow }) {
  return (
    <>
      <span className="dashboard__activity-glyph" aria-hidden="true">
        {row.glyph}
      </span>
      <span className="dashboard__activity-body">
        <span className="dashboard__activity-title">{row.title}</span>
        <span className="dashboard__activity-detail">{row.detail}</span>
      </span>
    </>
  );
}

/**
 * The quick actions, which are only the two ONDO can do.
 *
 * The design has a third — assign an agent — and the mandate table now exists,
 * so the reason it is still not here has changed. Assigning names a WORK, and
 * this panel has none: it would have to open a picker first, which is a second
 * screen wearing a quick action's clothes. It lives on the work's own record,
 * beside what it is over. See DASHBOARD_COPY.
 */
export function QuickActions({
  registerHref,
  collectionHref,
}: {
  registerHref: string;
  collectionHref: string;
}) {
  return (
    <div className="dashboard__actions">
      <h2 className="dashboard__actions-title">{DASHBOARD_COPY.quickActions}</h2>

      <Link to={registerHref} className="dashboard__action">
        <span className="dashboard__action-glyph" aria-hidden="true">
          ✦
        </span>
        <span className="dashboard__action-label">{DASHBOARD_COPY.register}</span>
        <span className="dashboard__action-chevron" aria-hidden="true">
          ›
        </span>
      </Link>

      <Link to={collectionHref} className="dashboard__action">
        <span className="dashboard__action-glyph" aria-hidden="true">
          ◫
        </span>
        <span className="dashboard__action-label">{DASHBOARD_COPY.openCollection}</span>
        <span className="dashboard__action-chevron" aria-hidden="true">
          ›
        </span>
      </Link>
    </div>
  );
}
